import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../../context/ToastContext";

const WIDTH = 400;
const HEIGHT = 130;

/* Draws a data URL "contain"-fit and centred onto the canvas (reference loadImgToCanvas). */
const drawImage = (canvas, dataUrl, onDone) => {
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (!dataUrl) return;
  const img = new Image();
  img.onload = () => {
    const ratio = Math.min(canvas.width / img.width, canvas.height / img.height);
    const x = (canvas.width - img.width * ratio) / 2;
    const y = (canvas.height - img.height * ratio) / 2;
    ctx.drawImage(img, 0, 0, img.width, img.height, x, y, img.width * ratio, img.height * ratio);
    if (onDone) onDone();
  };
  img.src = dataUrl;
};

/*
 * Touch/mouse signature canvas with image upload and clear, ported from the
 * reference. `initialValue` is drawn once on mount; every change reports the
 * canvas as a PNG data URL ("" when cleared) through onChange.
 */
export default function SignaturePad({ id, label, uploadedMessage, initialValue, onChange, children }) {
  const canvasRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [hasData, setHasData] = useState(!!initialValue);
  const showToast = useToast();

  onChangeRef.current = onChange;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let drawing = false;
    let dirty = false;

    if (initialValue) drawImage(canvas, initialValue);

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const point = e.touches ? e.touches[0] : e;
      return {
        x: (point.clientX - rect.left) * (canvas.width / rect.width),
        y: (point.clientY - rect.top) * (canvas.height / rect.height),
      };
    };

    const startDraw = (e) => {
      drawing = true;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (e.cancelable) e.preventDefault();
    };

    const moveDraw = (e) => {
      if (!drawing) return;
      const pos = getPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      dirty = true;
      if (e.cancelable) e.preventDefault();
    };

    const endDraw = () => {
      if (drawing && dirty) {
        setHasData(true);
        onChangeRef.current(canvas.toDataURL("image/png"));
      }
      drawing = false;
      dirty = false;
    };

    canvas.addEventListener("mousedown", startDraw);
    canvas.addEventListener("mousemove", moveDraw);
    window.addEventListener("mouseup", endDraw);
    canvas.addEventListener("touchstart", startDraw, { passive: false });
    canvas.addEventListener("touchmove", moveDraw, { passive: false });
    canvas.addEventListener("touchend", endDraw);

    return () => {
      canvas.removeEventListener("mousedown", startDraw);
      canvas.removeEventListener("mousemove", moveDraw);
      window.removeEventListener("mouseup", endDraw);
      canvas.removeEventListener("touchstart", startDraw);
      canvas.removeEventListener("touchmove", moveDraw);
      canvas.removeEventListener("touchend", endDraw);
    };
    // initialValue is only ever drawn on mount; later changes come from this pad itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = (e) => {
    const input = e.target;
    const file = input.files && input.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showToast("Please select a valid image file (PNG, JPG, SVG, WebP, etc.).", "error");
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      const canvas = canvasRef.current;
      drawImage(canvas, evt.target.result, () => {
        setHasData(true);
        onChangeRef.current(canvas.toDataURL("image/png"));
        showToast(uploadedMessage);
      });
      input.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasData(false);
    onChangeRef.current("");
  };

  return (
    <div className="form-group signature-box-card">
      <div className="sig-header">
        <label>{label}</label>
        <div className="sig-actions">
          <label htmlFor={`upload-${id}`} className="btn-upload-sig" title="Upload signature image from photo gallery or computer">
            📁 Upload Image
          </label>
          <input type="file" id={`upload-${id}`} accept="image/*" style={{ display: "none" }} onChange={handleUpload} />
          <button type="button" className="btn-text-sm" onClick={handleClear}>
            Clear
          </button>
        </div>
      </div>
      <div className="canvas-wrapper">
        <canvas id={`canvas-${id}`} ref={canvasRef} width={WIDTH} height={HEIGHT}></canvas>
        {!hasData && <span className="sig-placeholder-text">Sign using touch / mouse or upload image</span>}
      </div>
      {children}
    </div>
  );
}
